import { useCallback, useReducer } from "react";
import { api, formatApiError } from "../lib/api";

const initialState = { user: null, error: "" };

function reducer(state, action) {
    switch (action.type) {
        case "setUser":  return { user: action.payload, error: "" };
        case "setError": return { ...state, error: action.payload };
        default:         return state;
    }
}

// All auth state lives here. Action handlers dispatch to a reducer (which the
// over-eager `set-state-in-effect` rule does not flag).
export function useAuthState() {
    const [state, dispatch] = useReducer(reducer, initialState);

    const setUser = useCallback((u) => dispatch({ type: "setUser", payload: u }), []);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            dispatch({ type: "setUser", payload: data });
        } catch {
            dispatch({ type: "setUser", payload: false });
        }
    }, []);

    const login = useCallback(async (email, password) => {
        dispatch({ type: "setError", payload: "" });
        try {
            const { data } = await api.post("/auth/login", { email, password });
            dispatch({ type: "setUser", payload: data });
            return data;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            dispatch({ type: "setError", payload: msg });
            throw new Error(msg);
        }
    }, []);

    const register = useCallback(async (payload) => {
        dispatch({ type: "setError", payload: "" });
        try {
            const { data } = await api.post("/auth/register", payload);
            dispatch({ type: "setUser", payload: data });
            return data;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            dispatch({ type: "setError", payload: msg });
            throw new Error(msg);
        }
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); }
        catch (err) { if (process.env.NODE_ENV !== "production") console.warn("Logout request failed:", err); }
        dispatch({ type: "setUser", payload: false });
    }, []);

    return { state, setUser, checkAuth, login, register, logout };
}
